import React, { useState } from "react";
import { TransInterContainerProps } from "./types";
import {
  Box,
  createStyles,
  MantineTheme,
  rem,
  Space,
  Stepper,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { SHA256 } from "crypto-js";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import TransInterStepOne from "./trans-inter-step-1/TransInterStepOne";
import TransInterStepTwo from "./trans-inter-step-2/TransInterStepTwo";
import TransInterStepThree from "./trans-inter-step-3/TransInterStepThree";
import TransInterStepFour from "./trans-inter-step-4/TransInterStepFour";
import TransInterStepFive from "./trans-inter-step-5/TransInterStepFive";
import { TransFormProvider, useTransForm } from "./TransInterFormContext";
import { useLocation } from "react-router-dom";
import { RESULT_CODE } from "@/constants/enum";
import {
  useGetInternationalTransferMasterDataQuery,
  useInitConfirmInternationalTransferMutation,
  useMakerConfirmInternationalTransferMutation,
  useMakerInitInternationalTransferMutation,
} from "@/services/internationalTransferAPI";
import { computeIntlDebit } from "./debitUtils";
import { useGenerateOTPMutation } from "@/services/authAPI";
import {
  InitInternationalTransferResponse,
  ConfirmInternationalTransferResponse,
} from "@/dto/international-transfer/international-transfer";
import { getOtpPhoneNumber } from "@/utils/otpPhone";

// Định dạng ngày cam kết bổ sung chứng từ (Date) -> chuỗi dd/MM/yyyy khớp BE (JVC-3373).
const formatCommitmentDate = (d?: Date | null): string | undefined => {
  if (!d) return undefined;
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return undefined;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${dt.getFullYear()}`;
};

const useStylesStepper = createStyles((theme: MantineTheme) => ({
  stepIcon: {
    "&[data-progress]": {
      borderWidth: 0,
      backgroundColor: theme.colors.primary[7],
      color: "#fff",
    },
    "&[data-completed]": {
      borderWidth: 0,
      backgroundColor: theme.colors.green[7],
      color: "#fff",
    },
  },
}));

const toAmountNumber = (value?: string): number =>
  Number(String(value ?? "").replace(/[^\d.]/g, "")) || 0;

// Phương thức xác thực (khớp giá trị BE trả về trong authObjects) — như luồng CTTL lô.
const AUTH_TYPE_SMS_OTP = "1";
const AUTH_TYPE_SMART_OTP = "2";

const TransInternationalContainer: React.FC<TransInterContainerProps> = ({
  step,
  sourceLinkId,
  sources,
  onChangeSourceLink,
  onSubmitStepOne,
  onSubmitStepTwo,
  onSubmitStepThree,
  onSubmitStepFour,
  onBackStepOne,
  onBackStepTwo,
  onBackStepThree,
  onGoHome,
  onCreateNew,
}) => {
  const { t } = useTranslation();
  const { classes: classesStepper } = useStylesStepper();

  const location = useLocation();
  const prefillBeneficiary = (location.state as { beneficiary?: any })
    ?.beneficiary;

  const [initData, setInitData] =
    useState<InitInternationalTransferResponse | null>(null);
  const [confirmData, setConfirmData] =
    useState<ConfirmInternationalTransferResponse | null>(null);
  const [refId, setRefId] = useState("");
  const [otpId, setOtpId] = useState("");
  const [authType, setAuthType] = useState("1");

  const [makerInit] = useMakerInitInternationalTransferMutation();
  const [initConfirm] = useInitConfirmInternationalTransferMutation();
  const [makerConfirm] = useMakerConfirmInternationalTransferMutation();
  const [generateOTP] = useGenerateOTPMutation();
  const { data: masterData } = useGetInternationalTransferMasterDataQuery();

  const form = useTransForm({
    initialValues: {
      sourceAccount: "",
      currency: "USD",
      needBuyCurrency: "N",
      feeType: "OUR",
      beneficiaryAccount: prefillBeneficiary?.so_tai_khoan ?? "",
      beneficiaryName: prefillBeneficiary?.ten_nguoi_thu_huong ?? "",
      documents: [],
      debtDocumentLater: false,
    },
    validate: {
      amount: (value) => {
        const numeric = toAmountNumber(value);
        return numeric > 0 ? null : `${t("AMOUNT")} ${t("FIELD_REQUIRED")}`;
      },
      // Số tài khoản / IBAN — quốc gia suy từ mã SWIFT (BIC ký tự 5-6). BR-14
      beneficiaryAccount: (value, values) => {
        const acc = (value || "").trim();
        if (!acc) return `${t("ACCOUNT_NUMBER")} ${t("FIELD_REQUIRED")}`;
        const swift = (values.beneficiarySwiftCode || "").trim().toUpperCase();
        const country = swift.length >= 6 ? swift.substring(4, 6) : "";
        if (country === "EG") {
          return /^EG\d{2}[A-Za-z0-9]{25}$/.test(acc.toUpperCase())
            ? null
            : t("EGYPT_IBAN_RULE");
        }
        if (country === "AE") {
          return /^AE\d{2}[A-Za-z0-9]{19}$/.test(acc.toUpperCase())
            ? null
            : t("UAE_IBAN_RULE");
        }
        const upper = acc.toUpperCase();
        if (upper.length > 35) return t("ACCOUNT_NUMBER_MAX_35_CHARS");
        // Tài khoản thường (không phải IBAN EG/AE): chỉ cho nhập số
        return /^\d+$/.test(upper) ? null : t("ACCOUNT_NUMBER_DIGITS_ONLY");
      },
      beneficiaryName: (value) => {
        const v = (value || "").trim();
        if (!v) return `${t("BENEFICIARY_NAME")} ${t("FIELD_REQUIRED")}`;
        if (v.length > 35) return t("ACCOUNT_NAME_MAX_35_CHARS");
        return /^[A-Za-z0-9 .,+/-]*$/.test(v)
          ? null
          : t("ACCOUNT_NAME_MUST_BE_UNACCENTED");
      },
      beneficiaryAddress: (value) =>
        (value || "").length <= 200 ? null : t("ADDRESS_MAX_200_CHARACTERS"),
      beneficiarySwiftCode: (value) => {
        const v = (value || "").trim().toUpperCase();
        if (!v) return `${t("SWIFT_CODE")} ${t("FIELD_REQUIRED")}`;
        return /^[A-Za-z]{6}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?$/.test(v)
          ? null
          : t("TPL_FIELD_INVALID", { p0: t("SWIFT_CODE") });
      },
      beneficiaryBankName: (value) =>
        value?.trim() ? null : `${t("BANK_NAME")} ${t("FIELD_REQUIRED")}`,
      // Field 70/72: tối đa 200 ký tự + bộ ký tự SWIFT (không dấu tiếng Việt). BR-11
      detailContent: (value) => {
        const v = value || "";
        if (v.length > 200) return t("DETAIL_CONTENT_MAX_200");
        return /^[A-Za-z0-9 .,+/-]*$/.test(v)
          ? null
          : t("INVALID_CHARACTERS_ALLOWED_SET");
      },
      bankNotice: (value) => {
        const v = value || "";
        if (v.length > 200) return t("NOTICE_MAX_200_CHARACTERS");
        return /^[A-Za-z0-9 .,+/-]*$/.test(v)
          ? null
          : t("INVALID_CHARACTERS_ALLOWED_SET");
      },
      purposeType: (value) =>
        value?.trim() ? null : `${t("PURPOSE_TYPE")} ${t("FIELD_REQUIRED")}`,
      purposeDetail: (value) =>
        value?.trim()
          ? null
          : `${t("PURPOSE_OF_MONEY_TRANSFER")} ${t("FIELD_REQUIRED")}`,
    },
  });

  const showError = (message: string) =>
    notifications.show({
      title: t("NOTICE") || t("NOTICE"),
      message,
      color: "red",
    });

  // Bước 3 -> 4: gọi maker init để lấy phí/tổng tiền/tỷ giá thật từ BE
  const handleInitTransfer = async () => {
    const selected = sources.find((s) => s.id === sourceLinkId);
    const sourceAccountNo =
      selected?.sourceNumber || selected?.sourceObjectNumber || "";
    if (!sourceAccountNo) {
      showError(t("SOURCE_ACCOUNT_NOT_FOUND") || t("SOURCE_ACCOUNT_NOT_FOUND"));
      return;
    }
    form.setFieldValue("sourceAccountNo", sourceAccountNo);
    form.setFieldValue("sourceAccountName", selected?.sourceName || "");

    // Tổng số tiền ghi nợ FE tính (quy về loại tiền TK nguồn) — gửi lên BE.
    const debit = computeIntlDebit({
      amount: form.values.amount,
      currency: form.values.currency || "USD",
      feeType: form.values.feeType,
      foreignAmount: form.values.foreignAmount,
      needBuyCurrency: form.values.needBuyCurrency,
      accountCcy: selected?.currency,
      currencies: masterData?.body?.currencies,
    });

    try {
      const result = await makerInit({
        sourceAccountNo,
        feeSourceAccountNo: form.values.feeSourceAccountNo || sourceAccountNo,
        currency: form.values.currency || "USD",
        amount: toAmountNumber(form.values.amount),
        needBuyCurrency: form.values.needBuyCurrency || "N",
        feeType: form.values.feeType || "OUR",
        beneficiaryAccount: form.values.beneficiaryAccount || "",
        beneficiaryName: form.values.beneficiaryName || "",
        beneficiaryAddress: form.values.beneficiaryAddress || "",
        beneficiaryBankSwift: form.values.beneficiarySwiftCode || "",
        beneficiaryBankName: form.values.beneficiaryBankName || "",
        beneficiaryBankAddress: form.values.beneficiaryBankAddress || "",
        intermediaryBankSwift: form.values.intermediaryBankSwift || "",
        intermediaryBankName: form.values.intermediaryBankName || "",
        intermediaryBankAddress: form.values.intermediaryBankAddress || "",
        detailContent: form.values.detailContent || "",
        bankNotice: form.values.bankNotice || "",
        purposeType: form.values.purposeType || "",
        purposeDetail: form.values.purposeDetail || "",
        toCustType: form.values.toCustType || "",
        branchCode: form.values.branchCode || "",
        totalAmountForeign: debit.value,
        totalAmountForeignDisplay: debit.display,
        documents: form.values.documents || [],
        // Nợ chứng từ bổ sung sau (JVC-3373 P1): persist cờ + hạn cam kết để màn chi tiết hiển thị.
        debtDocumentLater: !!form.values.debtDocumentLater,
        commitmentDate: form.values.debtDocumentLater
          ? formatCommitmentDate(form.values.commitmentDate)
          : undefined,
      }).unwrap();

      const body = result?.body;
      if (body?.resultCode === RESULT_CODE.SUCCESS) {
        setInitData(body);
        setRefId(body.refId || "");
        setAuthType(body.authObjects?.[0]?.authType || "1");
        onSubmitStepThree?.();
      } else {
        showError(body?.resultDesc || t("ERROR_OCCURRED") || t("ERROR_OCCURRED"));
      }
    } catch (error) {
      showError(
        get(error, "data", "") || t("CONNECTION_ERROR") || t("CONNECTION_ERROR"),
      );
    }
  };

  // Bước 4: bấm "Xác nhận" -> gọi init xác nhận trước khi mở modal OTP.
  // Bám ĐÚNG cấu trúc chuyển tiền theo lô (handleSubmitStepTwo):
  //   1) initConfirm (gọi cho CẢ SMS lẫn Smart OTP)  == initBatchTransfer của lô
  //   2) Smart OTP: dùng refId làm otpId, mở modal luôn (KHÔNG gọi generateOTP);
  //      người dùng lấy mã 6 số từ app KeyPass.
  //   3) SMS OTP: gọi generateOTP để gửi mã về máy rồi mới mở modal.
  const handleRequestOtp = async (): Promise<boolean> => {
    try {
      // (1) Init bước xác nhận — luôn gọi khi bấm nút, giống initBatchTransfer của lô.
      const initRes = await initConfirm({ refId, authType }).unwrap();
      if (initRes?.body?.resultCode !== RESULT_CODE.SUCCESS) {
        showError(
          initRes?.body?.resultDesc || t("ERROR_OCCURRED") || t("ERROR_OCCURRED"),
        );
        return false;
      }

      // (2) Smart OTP: mã sinh trên app KeyPass, otpId = refId (như lô dùng transactionId).
      if (authType === AUTH_TYPE_SMART_OTP) {
        setOtpId(refId);
        return true;
      }

      // (3) SMS OTP: phát sinh mã gửi về máy.
      const res = await generateOTP({
        otpType: "SMS_OTP",
        objectId: refId,
        objectType: "international_transfer",
        phoneNumber: getOtpPhoneNumber(),
      } as any).unwrap();
      if (res?.body?.resultCode === RESULT_CODE.SUCCESS) {
        setOtpId(get(res, "body.otpId", ""));
        return true;
      }
      showError(res?.body?.resultDesc || t("ERROR_OCCURRED") || t("ERROR_OCCURRED"));
      return false;
    } catch (error) {
      showError(
        get(error, "data", "") || t("CONNECTION_ERROR") || t("CONNECTION_ERROR"),
      );
      return false;
    }
  };

  // Bước 4: xác nhận OTP -> confirm giao dịch -> sang bước 5.
  // Smart OTP: gửi mã 6 số nguyên bản để KeyPass xác thực; SMS OTP: hash SHA-256.
  const handleConfirmOtp = async (otp: string): Promise<boolean> => {
    try {
      const isSmartOtp = authType === AUTH_TYPE_SMART_OTP;
      const res = await makerConfirm({
        refId,
        authObject: {
          authType,
          otpId,
          otp: isSmartOtp ? otp || "" : SHA256(otp || "").toString(),
        },
      }).unwrap();
      if (res?.body?.resultCode === RESULT_CODE.SUCCESS) {
        setConfirmData(res.body);
        onSubmitStepFour?.();
        return true;
      }
      showError(res?.body?.resultDesc || t("ERROR_OCCURRED") || t("ERROR_OCCURRED"));
      return false;
    } catch (error) {
      showError(
        get(error, "data", "") || t("CONNECTION_ERROR") || t("CONNECTION_ERROR"),
      );
      return false;
    }
  };

  return (
    <div>
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px",
          width: "100%",
        }}
      >
        <Box
          style={{
            fontWeight: 800,
            fontSize: "24px",
          }}
        >
          {t("INTERNATIONAL_TRANSFER")}
        </Box>
        {step !== 5 && (
          <Stepper
            completedIcon={<IconCheck />}
            w="70%"
            active={step - 1}
            breakpoint="md"
            classNames={classesStepper}
            allowNextStepsSelect={false}
          >
            <Stepper.Step label={t("INFORMATION_TRANSFER")} />
            <Stepper.Step label={t("BENEFICIARY")} />
            <Stepper.Step label={t("FILE")} />
            <Stepper.Step label={t("CONFIRM")} />
          </Stepper>
        )}
      </Box>
      <TransFormProvider form={form}>
        <Space h={rem(30)} />
        {step === 1 && (
          <TransInterStepOne
            onChangeSourceLink={onChangeSourceLink}
            sourceLinkId={sourceLinkId}
            sources={sources}
            onSubmit={onSubmitStepOne}
          />
        )}
        {step === 2 && (
          <TransInterStepTwo
            onSubmit={onSubmitStepTwo}
            onBackStep={onBackStepOne}
          />
        )}
        {step === 3 && (
          <TransInterStepThree
            onSubmit={handleInitTransfer}
            onBackStep={onBackStepTwo}
          />
        )}
        {step === 4 && (
          <TransInterStepFour
            onBackStep={onBackStepThree}
            sources={sources}
            sourceLinkId={sourceLinkId}
            onChangeSourceLink={onChangeSourceLink}
            initData={initData}
            approvalProcessInfo={initData?.approvalProcessInfo}
            onRequestOtp={handleRequestOtp}
            onConfirmOtp={handleConfirmOtp}
            authType={authType}
            onChangeAuthType={setAuthType}
          />
        )}
        {step === 5 && (
          <TransInterStepFive
            initData={initData}
            confirmData={confirmData}
            onGoHome={onGoHome}
            onCreateNew={onCreateNew}
          />
        )}
      </TransFormProvider>
    </div>
  );
};

export default TransInternationalContainer;
